/**
 * Utility functions for subscription management
 */

/**
 * Check if a user has an active subscription
 * @param {Object} subscription - The subscription object from user data
 * @returns {boolean} - True if user has active subscription
 */
export const isSubscriptionActive = (subscription) => {
  if (!subscription) return false;
  
  // Check if subscription status is ACTIVE
  if (subscription.status !== 'ACTIVE') return false;
  
  // Check if next billing time is in the future
  if (subscription.nextBillingTime) {
    const nextBilling = new Date(subscription.nextBillingTime);
    const now = new Date();
    if (nextBilling <= now) return false;
  }
  
  return true;
};

/**
 * Check if a subscription has expired based on nextBillingTime
 * @param {Object} subscription - The subscription object from user data
 * @returns {boolean} - True if subscription has expired
 */
export const isSubscriptionExpired = (subscription) => {
  if (!subscription?.nextBillingTime) return true;
  
  const nextBilling = new Date(subscription.nextBillingTime);
  const now = new Date();
  
  return nextBilling <= now;
};

/**
 * Get days until subscription expires
 * @param {Object} subscription - The subscription object from user data
 * @returns {number} - Days until expiration (negative if expired)
 */
export const getDaysUntilExpiration = (subscription) => {
  if (!subscription?.nextBillingTime) return -1;
  
  const nextBilling = new Date(subscription.nextBillingTime);
  const now = new Date();
  const diffTime = nextBilling - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
};

/**
 * Check if a user is subscribed (legacy compatibility)
 * @param {Object} user - The user object
 * @returns {boolean} - True if user has active subscription
 */
export const isUserSubscribed = (user) => {
  return isSubscriptionActive(user?.subscription);
};

/**
 * Check if subscription needs server-side validation
 * @param {Object} subscription - The subscription object from user data
 * @returns {boolean} - True if we should check with server
 */
export const shouldCheckWithServer = (subscription) => {
  if (!subscription) return false;
  
  // If status is ACTIVE but nextBillingTime is in the past, check with server
  if (subscription.status === 'ACTIVE' && subscription.nextBillingTime) {
    const nextBilling = new Date(subscription.nextBillingTime);
    const now = new Date();
    return nextBilling <= now;
  }
  
  return false;
};

/**
 * Get subscription status for display
 * @param {Object} subscription - The subscription object
 * @returns {string} - Human readable status
 */
export const getSubscriptionStatus = (subscription) => {
  if (!subscription) return 'No Subscription';
  
  switch (subscription.status) {
    case 'APPROVAL_PENDING':
      return 'Pending Approval';
    case 'APPROVED':
      return 'Approved';
    case 'ACTIVE':
      return 'Active';
    case 'SUSPENDED':
      return 'Suspended';
    case 'CANCELLED':
      return 'Cancelled';
    case 'EXPIRED':
      return 'Expired';
    default:
      return 'Unknown';
  }
};

/**
 * Get detailed subscription status with access information
 * @param {Object} subscription - The subscription object
 * @returns {Object} - Status object with type and message
 */
export const getDetailedSubscriptionStatus = (subscription) => {
  if (!subscription) {
    return {
      type: 'noSubscription',
      message: 'No Subscription',
      hasAccess: false
    };
  }

  const now = new Date();
  
  if (subscription.status === 'ACTIVE') {
    return {
      type: 'subscribed',
      message: 'Subscribed',
      hasAccess: true
    };
  }
  
  if (subscription.status === 'CANCELLED') {
    if (subscription.nextBillingTime) {
      const nextBilling = new Date(subscription.nextBillingTime);
      
      if (nextBilling > now) {
        return {
          type: 'canceledStillActive',
          message: 'Canceled but still active',
          hasAccess: true,
          endDate: nextBilling
        };
      } else {
        return {
          type: 'canceledExpired',
          message: 'Canceled and expired',
          hasAccess: false,
          endDate: nextBilling
        };
      }
    } else {
      return {
        type: 'canceledExpired',
        message: 'Canceled',
        hasAccess: false
      };
    }
  }
  
  // Handle other statuses
  return {
    type: subscription.status?.toLowerCase() || 'unknown',
    message: getSubscriptionStatus(subscription),
    hasAccess: false
  };
};

/**
 * Get subscription plan display name
 * @param {Object} subscription - The subscription object
 * @returns {string} - Human readable plan name
 */
export const getSubscriptionPlan = (subscription) => {
  if (!subscription?.planType) return 'No Plan';
  
  switch (subscription.planType) {
    case 'monthly':
      return 'Monthly';
    case 'yearly':
      return 'Yearly';
    default:
      return subscription.planType;
  }
};

/**
 * Format next billing date
 * @param {Object} subscription - The subscription object
 * @returns {string} - Formatted date string
 */
export const getNextBillingDate = (subscription) => {
  if (!subscription?.nextBillingTime) return 'Unknown';
  
  try {
    const date = new Date(subscription.nextBillingTime);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    
    return date.toLocaleDateString();
  } catch (error) {
    console.error('Error parsing nextBillingTime:', error);
    return 'Invalid Date';
  }
};

/**
 * Get subscription plan price
 * @param {Object} subscription - The subscription object
 * @param {string} locale - The user's locale ('en' or 'he')
 * @returns {string} - Formatted price with currency
 */
export const getSubscriptionPrice = (subscription, locale = 'en') => {
  if (!subscription?.price) return 'No Price';
  
  // Price is now fetched live from PayPal API
  return `${subscription.currency} ${subscription.price}`;
}; 